import { z } from 'zod';
/**
 * Todo — a personal task tracker scoped to an org. Each todo is owned
 * by exactly one user (`ownerUid`) and visible only to them within
 * that org. NOT a project-management subsystem; explicitly a personal
 * tracker (a checkbox on the user's own list).
 *
 * Two creation paths:
 *   1. Manual — user clicks "+ New todo" on the /todos page.
 *   2. Promoted from a note — user clicks the "→ Todo" affordance on
 *      a `- [ ]` checkbox in a note. The new todo carries `noteId` +
 *      `noteCheckboxId` so toggling the todo's status can round-trip
 *      back to the source note (best-effort).
 *
 * Optionally linked to an entity (the dossier the work is about) so
 * todos can be filtered to "what I'm doing for customer X" — links
 * are display-only; the privacy model stays owner-scoped.
 *
 * Storage path: `todos/{todoId}`. Firestore rules enforce owner-only
 * read AND owner-only create/update/delete. Production traffic flows
 * through `/api/todos*` so the server stamps timestamps + emits audit
 * events.
 */
export const TodoStatus = z.enum(['todo', 'in_progress', 'done']);
export const TodoPriority = z.enum(['low', 'normal', 'high']);
export const Todo = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    ownerUid: z.string().min(1),
    title: z.string().min(1).max(280),
    notes: z.string().max(4_000).optional(),
    status: TodoStatus,
    priority: TodoPriority,
    /** Optional display name of the linked entity (filter-only). */
    entityName: z.string().optional(),
    /** Optional link to an investigation (legacy field; unused in LeanOpx). */
    investigationId: z.string().optional(),
    /**
     * Source-note linkage when the todo was promoted from an in-note
     * checkbox. `noteId` lets the /todos page render a "from note: …"
     * chip linking back; `noteCheckboxId` is the TipTap node id stamped
     * on the original `- [ ]` so a status flip on the todo can locate +
     * toggle the checkbox in the note's body (best-effort round-trip).
     */
    noteId: z.string().min(1).optional(),
    noteCheckboxId: z.string().min(1).optional(),
    /** ISO date (YYYY-MM-DD) — date-only, no time part. */
    dueDate: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    completedAt: z.string().optional(),
});
export const CreateTodoRequest = z
    .object({
    title: z.string().min(1).max(280),
    notes: z.string().max(4_000).optional(),
    status: TodoStatus.default('todo'),
    priority: TodoPriority.default('normal'),
    entityName: z.string().optional(),
    investigationId: z.string().optional(),
    /**
     * Promote-from-note shortcut: when both fields are present, the
     * server records the linkage so a later status flip can round-trip
     * to the source note. The schema enforces both-or-neither via the
     * refine below — `noteCheckboxId` alone is meaningless (we'd have
     * no note to look up against) and `noteId` alone defeats the
     * round-trip's purpose.
     */
    noteId: z.string().min(1).optional(),
    noteCheckboxId: z.string().min(1).optional(),
    dueDate: z.string().optional(),
})
    .refine((req) => {
    const hasNote = req.noteId !== undefined;
    const hasCb = req.noteCheckboxId !== undefined;
    return hasNote === hasCb;
}, {
    message: 'noteId and noteCheckboxId must be supplied together (or both omitted)',
    path: ['noteCheckboxId'],
});
export const UpdateTodoRequest = z.object({
    title: z.string().min(1).max(280).optional(),
    notes: z.string().max(4_000).optional(),
    status: TodoStatus.optional(),
    priority: TodoPriority.optional(),
    entityName: z.string().nullable().optional(),
    investigationId: z.string().nullable().optional(),
    dueDate: z.string().nullable().optional(),
});
export const ListTodosResponse = z.object({
    todos: z.array(Todo),
});
